/**
 * One-shot boot hook that silently upgrades a LEGACY session into an access +
 * refresh pair via `POST /api/auth/v1/session/upgrade`.
 *
 * A legacy session is an access token minted by the pre-refresh-token signer:
 * present and unexpired, with NO stored refresh token and NO `typ` claim in the
 * payload (the new signer stamps `typ: 'access'`; no legacy signer ever emits
 * it).
 *
 * Fire-and-forget by design: any failure (network, 4xx) keeps the legacy
 * session exactly as it was — one `console.warn`, no retry — and the
 * module-scoped once-guard (same idea as the provider's `exchangedHandoffTokens`
 * ref) caps the attempt at one per page lifetime, StrictMode double-mounts
 * included. Never runs while impersonating: the tab-scoped impersonation token
 * must not mint a refresh pair over the admin's underlying session (see
 * docs/impersonation.md).
 */
import { jwtDecode } from "jwt-decode";

// Deep imports rather than the `@/contexts` barrel — same reasoning as
// `tokenRefresh.ts`: the barrel pulls in every provider and would cycle back
// through the transports.
import {
  getAccessToken,
  getRefreshToken,
  isTokenExpired,
} from "@/contexts/AuthContext/AuthContext.utils";

import { upgradeSession } from "./authApi";
import { isImpersonating } from "./impersonation";
import { storeTokenPair } from "./tokenRefresh";

let hasAttemptedUpgrade = false;

const hasTypClaim = (accessToken: string): boolean => {
  try {
    const { typ } = jwtDecode<{ typ?: unknown }>(accessToken);
    return typeof typ === "string" && typ.length > 0;
  } catch {
    return false;
  }
};

/**
 * Detects a legacy session and exchanges it for a token pair, persisting the
 * pair through {@link storeTokenPair} (access token cell + refresh token +
 * expiry hint, with the `local-storage` event dispatch that keeps the
 * AuthContext in sync). Resolves without throwing in every case; at most one
 * attempt (and one warning) per page lifetime.
 */
export const upgradeLegacySession = async (): Promise<void> => {
  if (hasAttemptedUpgrade) return;
  hasAttemptedUpgrade = true;

  if (isImpersonating()) return;
  if (getRefreshToken()) return;

  const accessToken = getAccessToken();
  if (!accessToken || isTokenExpired(accessToken) || hasTypClaim(accessToken)) return;

  try {
    const tokenPair = await upgradeSession(accessToken);

    // The session may have been replaced or torn down while the upgrade was in
    // flight (logout, inbound portal/handoff token) — don't resurrect it.
    if (getAccessToken() !== accessToken) return;

    storeTokenPair(tokenPair);
  } catch (error) {
    console.warn("Legacy session upgrade failed; keeping the legacy session:", error);
  }
};
